import { useSyncExternalStore } from 'react';
import {
  askApiAiAskPost,
  getUnreadCountApiAiUnreadCountGet,
  markChatSeenApiAiRecentQuestionIdSeenPost,
  type AiRecentChat,
  type AiSource,
} from '@/lib/api/generated';

export interface ChatMessage {
  id: string;
  from: 'me' | 'sunny';
  text: string;
  sources?: AiSource[];
  result?: string;
}

/**
 * The Yakson AI chat lives outside React so it survives page changes (every page
 * mounts its own header): closing the panel or moving to another screen loses
 * nothing (GEN-12-02). It is emptied when a different person signs in.
 */
let state: { userId: string | null; messages: ChatMessage[]; typing: boolean; home: boolean; unread: number } = {
  userId: null,
  messages: [],
  typing: false,
  home: true,
  unread: 0,
};
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());
const set = (next: Partial<typeof state>) => {
  state = { ...state, ...next };
  emit();
};

let seq = 0;
const nextId = () => `m${++seq}`;

export function useChat() {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => state,
  );
}

/** Start a fresh chat when the signed-in person changes. */
export function bindChatToUser(userId: string | null) {
  if (state.userId !== userId) set({ userId, messages: [], typing: false, home: true, unread: 0 });
}

export async function sendQuestion(question: string, language: string, failText: string) {
  const q = question.trim();
  if (!q || state.typing) return;
  set({ messages: [...state.messages, { id: nextId(), from: 'me', text: q }], typing: true, home: false });
  try {
    const r = await askApiAiAskPost({ question: q, language });
    set({
      messages: [
        ...state.messages,
        { id: nextId(), from: 'sunny', text: r.answer, sources: r.sources ?? [], result: r.result },
      ],
      typing: false,
    });
  } catch {
    set({ messages: [...state.messages, { id: nextId(), from: 'sunny', text: failText }], typing: false });
  }
}

/** Back to the panel's home (Ask Sunny · Recent chats · Topics); the chat is kept. */
export function showHome() {
  set({ home: true });
}

/** GEN-12-02 — the number on the ✦ Yakson AI icon: her own unread HQ answers. */
export function setUnread(count: number) {
  if (state.unread !== count) set({ unread: count });
}

export async function refreshUnread() {
  if (!state.userId) return;
  try {
    setUnread((await getUnreadCountApiAiUnreadCountGet()).count ?? 0);
  } catch {
    // The badge simply stays as it was.
  }
}

/**
 * GEN-12-02 / -15 — reopen one of her recent chats to read it. If HQ answered it,
 * Sunny's message with HQ's answer comes last, and opening it marks it seen.
 */
export async function openRecentChat(
  chat: AiRecentChat,
  format: (question: string, answer: string) => string,
  sourceTitle: (date: string) => string,
) {
  const messages: ChatMessage[] = [
    { id: nextId(), from: 'me', text: chat.question },
    { id: nextId(), from: 'sunny', text: chat.answer, sources: chat.sources ?? [], result: chat.result },
  ];
  if (chat.hqAnswer && chat.hqAnsweredAt) {
    messages.push({
      id: nextId(),
      from: 'sunny',
      text: format(chat.question, chat.hqAnswer),
      sources: [{ title: sourceTitle(new Date(chat.hqAnsweredAt).toLocaleDateString('en-GB')), version: '' }],
      result: 'FIXED',
    });
  }
  set({ messages, typing: false, home: false });
  if (chat.unread) {
    try {
      setUnread((await markChatSeenApiAiRecentQuestionIdSeenPost(chat.id)).count ?? 0);
    } catch {
      // It stays unread and is marked the next time she opens it.
    }
  }
}
